// app.js
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.min.css';
import Layout from './layouts/Layout';
import MainLayout from './layouts/MainLayout';
import ProtectedRoute from './ProtectedRoute';
import Public from 'components/public';
import { useAuth } from 'hooks/useAuth';
import Loader from 'components/common/Loader';
import OfflineIndicator from 'components/errors/OfflineIndicator';
import CreateFarmer from 'screens/FarmerReport/CreateFarmer';
import CultivationForm from 'screens/Cultivation/Cultivationform';
import LoginPage from 'authScreen/login';
import OfflineData from 'screens/OfflineData';
import EditFarmer from 'screens/FarmerReport/EditFarmer';
import { toast, ToastContainer } from 'react-toastify';
import { CloseButton } from 'components/common/Toast';
import DynamicOfflineReport from 'screens/common/dynamicOfflineReport';
import CreateFarm from 'screens/FarmerReport/CreateFarm';
import EditFarm from 'screens/FarmerReport/EditFarm';

const App = () => {
  const { isAuthenticated, isLoading, isOffline, user, getDefaultRoute } =
    useAuth();

  // Add debugging
  // React.useEffect(() => {
  //   console.log('App render state:', {
  //     isAuthenticated,
  //     isLoading,
  //     isOffline,
  //     user,
  //     pathname: window.location.pathname
  //   });
  // }, [isAuthenticated, isLoading, isOffline, user]);

  if (isLoading) {
    return <Loader />;
  }

  // Get tenant ID from user object or use default
  const tenantId = user?.tenant_id;

  return (
    <>
      {/* Only show offline indicator when offline and not on the offline homepage */}
      {/* {isOffline && window.location.pathname !== '/offline' && (
        <OfflineIndicator />
      )} */}

      <Router basename={process.env.PUBLIC_URL}>
        <Routes>
          {/* Public routes accessible without auth */}
          <Route path="/public" element={<Public />} />
          <Route path="/login" element={<LoginPage />} />

          {/* Add the missing offline route */}
          <Route path="/offline" element={<OfflineData />} />

          {/* Root path redirection - FIXED */}
          <Route
            path="/"
            element={(() => {
              // console.log('Root route evaluation:', {
              //   isAuthenticated,
              //   isOffline
              // });

              if (!isAuthenticated) {
                return <Navigate to="/login" replace />;
              }

              if (isOffline) {
                return <Navigate to="/offline" replace />;
              }

              // Get default route for authenticated online users
              const defaultRoute = getDefaultRoute();
              // console.log('Redirecting to default route:', defaultRoute);
              return <Navigate to={defaultRoute} replace />;
            })()}
          />

          {/* Offline routes - accessible with auth and wrapped in MainLayout */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/farmer/create" element={<CreateFarmer />} />
            <Route path="/farmer/edit/:id" element={<EditFarmer />} />
            <Route path="/farm/create" element={<CreateFarm />} />
            <Route path="/farm/edit/:id" element={<EditFarm />} />
            <Route
              path="/cultivation/create"
              element={<CultivationForm edit={false} />}
            />
            <Route
              path="/cultivation/edit/:id"
              element={<CultivationForm edit={true} />}
            />
            <Route
              path="/offline/farmer-report"
              element={<DynamicOfflineReport dataType="farmer" />}
            />
            <Route
              path="/offline/farm-report"
              element={<DynamicOfflineReport dataType="farmland" />}
            />
            <Route
              path="/offline/cultivation-report"
              element={<DynamicOfflineReport dataType="cultivation" />}
            />
            <Route path="/offline-data" element={<OfflineData />} />
          </Route>

          {/* All other routes go through standard layout */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
      <ToastContainer
        closeButton={CloseButton}
        icon={false}
        position={toast.POSITION.BOTTOM_RIGHT}
      />
    </>
  );
};

export default App;
